import {
  getNotFromSimpleFilter,
  getOperatorFromQueryFilter,
  getOperatorFromSimpleFilter,
} from '@/sections/insights/queryBuilder/entities/common/translators';
import type {
  SimpleFilter,
  SimpleFilterDefinition,
} from '@/sections/insights/queryBuilder/entities/types';
import { isLogicalFilter } from '@/sections/insights/queryBuilder/utils/filterTypeGuards';
import type { QueryFilter } from '@/types';

/**
 * Risk Name filter — searches Risk.name, Risk.title, and Vulnerability.title via INSTANCE_OF.
 *
 * translateForAPI produces an OR logical filter:
 *   OR(
 *     Risk.name <op> <value>,
 *     Risk.title <op> <value>,
 *     Vulnerability.title <op> <value>  (via INSTANCE_OF relationship)
 *   )
 *
 * This ensures "Name contains dfs-service-..." matches regardless of whether
 * the name lives on the Risk node (name or title) or the related Vulnerability node.
 */
export const RiskName: SimpleFilterDefinition = {
  apiField: 'name',
  uiField: 'Name',
  inputType: 'text',
  operators: ['is', 'is not', 'contains', 'starts with', 'ends with'],

  translateForUI: (queryFilter: QueryFilter): SimpleFilter => {
    // If this is the OR wrapper, extract value from the first child (Risk.name)
    if (isLogicalFilter(queryFilter)) {
      const children = queryFilter.value as QueryFilter[];
      const nameChild = children.find(c => c.field === 'name');
      const source = nameChild || children[0];
      return {
        field: 'Name',
        operator: getOperatorFromQueryFilter(source),
        value: source.value as string | string[],
      };
    }

    // Bare leaf fallback (legacy or direct name filter)
    return {
      field: 'Name',
      operator: getOperatorFromQueryFilter(queryFilter),
      value: queryFilter.value as string | string[],
    };
  },

  matchesQueryFilter: (queryFilter: QueryFilter): boolean => {
    // Match the OR wrapper pattern: OR(name, title, title+relationship)
    if (isLogicalFilter(queryFilter)) {
      const children = queryFilter.value as QueryFilter[];
      if (!Array.isArray(children)) return false;
      const hasName = children.some(c => c.field === 'name');
      const hasRiskTitle = children.some(
        c => c.field === 'title' && !c.relationshipNodeLabel
      );
      const hasVulnTitle = children.some(
        c =>
          c.field === 'title' &&
          c.relationshipNodeLabel === 'Vulnerability' &&
          c.relationshipLabel === 'INSTANCE_OF'
      );
      // New 3-child pattern OR old 2-child pattern (backwards compat)
      return hasName && (hasRiskTitle || hasVulnTitle);
    }

    // Also match a bare name filter (no relationship) for backwards compat
    return queryFilter.field === 'name' && !queryFilter.relationshipNodeLabel;
  },

  translateForAPI: (simpleFilter: SimpleFilter): QueryFilter => {
    const operator = getOperatorFromSimpleFilter(simpleFilter);
    const not = getNotFromSimpleFilter(simpleFilter);

    return {
      field: '',
      operator: 'OR',
      value: [
        {
          field: 'name',
          operator,
          value: simpleFilter.value,
          not,
        },
        {
          field: 'title',
          operator,
          value: simpleFilter.value,
          not,
        },
        {
          field: 'title',
          operator,
          value: simpleFilter.value,
          not,
          relationshipNodeLabel: 'Vulnerability',
          relationshipDirection: 'target',
          relationshipLabel: 'INSTANCE_OF',
        },
      ],
    } as QueryFilter;
  },
};
